import { BaseTool } from './BaseTool';
import { ToolType } from '../types';

export class BrushTool extends BaseTool {
  public id: ToolType = 'brush';
  public name = 'Brush Tool';
  public cursor = 'none'; // custom circle cursor rendered in overlay

  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  private currentMouseX = 0;
  private currentMouseY = 0;

  public onMouseDown(e: MouseEvent, canvasX: number, canvasY: number): void {
    const layer = this.editor.getActiveLayer();
    if (!layer || layer.locked || !layer.visible) return;

    this.isDrawing = true;
    this.lastX = canvasX - layer.x;
    this.lastY = canvasY - layer.y;
    this.drawDot(layer, this.lastX, this.lastY);
    this.editor.render();
  }

  public onMouseMove(e: MouseEvent, canvasX: number, canvasY: number): void {
    this.currentMouseX = canvasX;
    this.currentMouseY = canvasY;

    if (!this.isDrawing) {
      this.editor.renderOverlayOnly();
      return;
    }

    const layer = this.editor.getActiveLayer();
    if (!layer || layer.locked || !layer.visible) return;

    const lx = canvasX - layer.x;
    const ly = canvasY - layer.y;

    this.drawLine(layer, this.lastX, this.lastY, lx, ly);
    this.lastX = lx;
    this.lastY = ly;
    this.editor.render();
  }

  public onMouseUp(_e: MouseEvent): void {
    if (this.isDrawing) {
      this.isDrawing = false;
      this.editor.recordHistory('Brush Stroke');
    }
  }

  private drawDot(layer: any, x: number, y: number) {
    const settings = this.editor.toolSettings;
    const radius = settings.brushSize / 2;
    const hardness = settings.brushHardness / 100;

    layer.ctx.save();
    layer.ctx.globalAlpha = settings.brushOpacity / 100;

    if (hardness >= 0.95) {
      layer.ctx.fillStyle = settings.brushColor;
      layer.ctx.beginPath();
      layer.ctx.arc(x, y, radius, 0, Math.PI * 2);
      layer.ctx.fill();
    } else {
      const grad = layer.ctx.createRadialGradient(x, y, radius * hardness, x, y, radius);
      grad.addColorStop(0, settings.brushColor);
      grad.addColorStop(1, 'transparent');
      layer.ctx.fillStyle = grad;
      layer.ctx.beginPath();
      layer.ctx.arc(x, y, radius, 0, Math.PI * 2);
      layer.ctx.fill();
    }
    layer.ctx.restore();
  }

  private drawLine(layer: any, x1: number, y1: number, x2: number, y2: number) {
    const dist = Math.hypot(x2 - x1, y2 - y1);
    const step = Math.max(1, (this.editor.toolSettings.brushSize / 2) * 0.25);
    const steps = Math.ceil(dist / step);

    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const x = x1 + (x2 - x1) * t;
      const y = y1 + (y2 - y1) * t;
      this.drawDot(layer, x, y);
    }
  }

  public renderOverlay(ctx: CanvasRenderingContext2D): void {
    const zoom = this.editor.zoom;
    const panX = this.editor.panX;
    const panY = this.editor.panY;

    const sx = this.currentMouseX * zoom + panX;
    const sy = this.currentMouseY * zoom + panY;
    const radius = (this.editor.toolSettings.brushSize / 2) * zoom;

    ctx.save();
    ctx.beginPath();
    ctx.arc(sx, sy, radius, 0, Math.PI * 2);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(sx, sy, radius, 0, Math.PI * 2);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 2]);
    ctx.lineDashOffset = 2;
    ctx.stroke();
    ctx.restore();
  }
}
